import { ADMIN_HOST, adminPath, isAdminHost, marketingHome } from './domains';
import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { Login } from './components/Login';
import { PageTitle } from './components/PageTitle';
import { HomePage } from './components/marketing/HomePage';
import { ContactPage } from './components/marketing/ContactPage';
import { MarketingLayout } from './components/marketing/MarketingLayout';
import { PrivacyPage } from './components/marketing/PrivacyPage';
import { SupportPage } from './components/marketing/SupportPage';
import { TermsPage } from './components/marketing/TermsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Every admin route is lazy-loaded — the console is a single-user-at-a-time
// staff tool where cold-start latency on first navigation matters far less
// than shipping the smallest possible bundle to the marketing pages (public,
// unauthenticated, and what most visitors actually hit).
const Applications = lazy(() =>
  import('./routes/Applications').then((m) => ({ default: m.Applications })),
);
const AuditLog = lazy(() =>
  import('./routes/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const Chats = lazy(() => import('./routes/Chats').then((m) => ({ default: m.Chats })));
const Dashboard = lazy(() =>
  import('./routes/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const Disputes = lazy(() =>
  import('./routes/Disputes').then((m) => ({ default: m.Disputes })),
);
const Moderation = lazy(() =>
  import('./routes/Moderation').then((m) => ({ default: m.Moderation })),
);
const SafetyIncidents = lazy(() =>
  import('./routes/SafetyIncidents').then((m) => ({ default: m.SafetyIncidents })),
);
const Drivers = lazy(() =>
  import('./routes/Drivers').then((m) => ({ default: m.Drivers })),
);
const FoodOrderDetail = lazy(() =>
  import('./routes/JobDetail').then((m) => ({ default: m.FoodOrderDetail })),
);
const PalengkeDetail = lazy(() =>
  import('./routes/JobDetail').then((m) => ({ default: m.PalengkeDetail })),
);
const ParcelDetail = lazy(() =>
  import('./routes/JobDetail').then((m) => ({ default: m.ParcelDetail })),
);
const RideDetail = lazy(() =>
  import('./routes/JobDetail').then((m) => ({ default: m.RideDetail })),
);
const FoodOrders = lazy(() =>
  import('./routes/FoodOrders').then((m) => ({ default: m.FoodOrders })),
);
const LiveMap = lazy(() => import('./routes/LiveMap').then((m) => ({ default: m.LiveMap })));
const Merchants = lazy(() =>
  import('./routes/Merchants').then((m) => ({ default: m.Merchants })),
);
const MerchantCatalog = lazy(() =>
  import('./routes/MerchantCatalog').then((m) => ({ default: m.MerchantCatalog })),
);
const PaymentsSettings = lazy(() =>
  import('./routes/PaymentsSettings').then((m) => ({ default: m.PaymentsSettings })),
);
const Refunds = lazy(() =>
  import('./routes/Refunds').then((m) => ({ default: m.Refunds })),
);
const Payouts = lazy(() =>
  import('./routes/Payouts').then((m) => ({ default: m.Payouts })),
);
const Remittances = lazy(() =>
  import('./routes/Remittances').then((m) => ({ default: m.Remittances })),
);
const Palengke = lazy(() =>
  import('./routes/Palengke').then((m) => ({ default: m.Palengke })),
);
const Parcels = lazy(() =>
  import('./routes/Parcels').then((m) => ({ default: m.Parcels })),
);
const People = lazy(() => import('./routes/People').then((m) => ({ default: m.People })));
const Rides = lazy(() => import('./routes/Rides').then((m) => ({ default: m.Rides })));
const Staff = lazy(() => import('./routes/Staff').then((m) => ({ default: m.Staff })));

// Public, unauthenticated, and reachable on either host: a shared trip
// link has to open for whoever it was texted to, with no account and no
// app. Lazy like the admin routes so its map code stays out of the
// marketing bundle.
const FollowTrip = lazy(() =>
  import('./routes/FollowTrip').then((m) => ({ default: m.FollowTrip })),
);

const PayReturn = lazy(() =>
  import('./routes/PayReturn').then((m) => ({ default: m.PayReturn })),
);

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <ScrollManager />
        <Routes>
          {!isAdminHost && <Route element={<MarketingLayout />}>
            <Route index element={<HomePage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="terms" element={<TermsPage />} />
          </Route>}

          <Route
            path="/pay/done"
            element={
              <Suspense fallback={null}>
                <PayReturn outcome="done" />
              </Suspense>
            }
          />
          <Route
            path="/pay/cancelled"
            element={
              <Suspense fallback={null}>
                <PayReturn outcome="cancelled" />
              </Suspense>
            }
          />
          <Route
            path="/t/:token"
            element={
              <Suspense fallback={null}>
                <FollowTrip />
              </Suspense>
            }
          />

          {isAdminHost && <Route path="/admin/*" element={<LegacyAdminRedirect />} />}
          <Route path={adminPath()} element={<AdminGate />}>
            <Route element={<AdminRouteSuspense />}>
              <Route index element={<Dashboard />} />
              <Route path="rides" element={<Rides />} />
              <Route path="rides/:id" element={<RideDetail />} />
              <Route path="parcels" element={<Parcels />} />
              <Route path="parcels/:id" element={<ParcelDetail />} />
              <Route path="papalit" element={<Palengke />} />
              <Route path="papalit/:id" element={<PalengkeDetail />} />
              <Route path="palengke" element={<Navigate to={adminPath("/admin/papalit")} replace />} />
              <Route path="food-orders" element={<FoodOrders />} />
              <Route path="food-orders/:id" element={<FoodOrderDetail />} />
              <Route path="map" element={<LiveMap />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="people" element={<People />} />
              <Route path="chats" element={<Chats />} />
              <Route path="merchants" element={<Merchants />} />
              <Route path="merchants/:uid" element={<MerchantCatalog />} />
              <Route path="payouts" element={<Payouts />} />
              <Route path="payments" element={<PaymentsSettings />} />
              <Route path="refunds" element={<Refunds />} />
              <Route path="remittances" element={<Remittances />} />
              <Route path="disputes" element={<Disputes />} />
              <Route path="moderation" element={<Moderation />} />
              <Route path="safety" element={<SafetyIncidents />} />
              <Route path="applications" element={<Applications />} />
              <Route
                path="staff"
                element={
                  <AdminOnly>
                    <Staff />
                  </AdminOnly>
                }
              />
              <Route
                path="audit"
                element={
                  <AdminOnly>
                    <AuditLog />
                  </AdminOnly>
                }
              />
              <Route path="*" element={<Navigate to={adminPath()} replace />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

/** Preserve bookmarked paths, search filters, and anchors when moving hosts. */
function LegacyAdminRedirect() {
  const { pathname, search, hash } = useLocation();
  const path = pathname.replace(/^\/admin(?=\/|$)/, '') || '/';
  const destination = `https://${ADMIN_HOST}${path}${search}${hash}`;
  useEffect(() => {
    if (!isAdminHost) window.location.replace(destination);
  }, [destination]);
  return isAdminHost
    ? <Navigate to={`${path}${search}${hash}`} replace />
    : <a href={destination}>Open Dala admin</a>;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const timer = window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
      return () => window.clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

/**
 * Staff console. Public marketing pages stay reachable without a session;
 * only `/admin` asks for email sign-in and a support/admin role.
 */
function AdminGate() {
  const { user, loading, isStaff, signOut } = useAuth();

  if (window.location.hostname === 'dala.delivery' || window.location.hostname === 'www.dala.delivery') {
    return <LegacyAdminRedirect />;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <PageTitle>dala console</PageTitle>
        <div className="h-1 w-10 rounded-full bg-mango" />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  if (!isStaff) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4">
        <PageTitle>dala console</PageTitle>
        <div className="card max-w-md p-8 text-center">
          <h1 className="text-xl">Not authorized</h1>
          <p className="mt-2 text-sm font-medium text-ink/50">
            This account does not have ops staff access. Ask an admin to set{' '}
            <code>role: support</code> on your user document.
          </p>
          <button className="btn-outline mt-6" onClick={() => void signOut()}>
            Sign out
          </button>
          <Link to={marketingHome} className="mt-4 block text-sm font-bold text-terracotta">
            Back to dala
          </Link>
        </div>
      </div>
    );
  }

  return <AppLayout />;
}

/** Fallback while a lazy-loaded admin route's chunk downloads. */
function AdminRouteSuspense() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="h-1 w-10 animate-pulse rounded-full bg-mango" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to={adminPath()} replace />;
  return <>{children}</>;
}
